import React, { useState, useEffect, useRef, cloneElement, isValidElement } from 'react';
import PropTypes from 'prop-types';
import { FaPlus } from 'react-icons/fa';
import './FAB.scss';

/**
 * Floating Action Button (FAB) component
 * @param {Object} props Component props
 * @param {Function} props.onClick Click handler function for the main button (when no options are provided)
 * @param {React.Component} props.icon React icon component to display (default: FaPlus)
 * @param {string} props.className Additional CSS class names
 * @param {string} props.color Button color (primary, success, danger, warning)
 * @param {string} props.tooltip Tooltip text for the button
 * @param {string} props.position Position of the FAB ('bottom-right', 'bottom-left', 'top-right', 'top-left')
 * @param {Array} props.options Array of option objects with icon, color, tooltip, onClick/component, and isVisible properties
 * @param {string} props.direction Direction in which options open ('up', 'down', 'left', 'right')
 * @param {Object} props.offset Optional offset values for x and y positions (in pixels)
 * @param {React.Component} props.component Optional React component (like Link) to use instead of button
 * @param {Object} props.componentProps Props to pass to the component
 * @param {boolean} props.draggable Enable draggable functionality (default: false)
 */
const FAB = ({
  onClick,
  icon = FaPlus,
  className = '',
  color = 'primary',
  tooltip = '',
  position = 'bottom-right',
  options = [],
  direction = 'up',
  offset = { x: 0, y: 0 },
  component,
  componentProps = {},
  draggable = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState(null);
  const dragRef = useRef(null);
  const dragStartPos = useRef({ x: 0, y: 0 });
  
  const baseClass = 'fab-button';
  const colorClass = `fab-${color}`;
  const positionClass = `fab-${position}`;
  const directionClass = `fab-direction-${direction}`;
  
  // Create style with offset and drag position
  const offsetStyle = {};
  
  if (draggable && dragPosition) {
    // Use absolute positioning when dragged
    offsetStyle.position = 'fixed';
    offsetStyle.right = 'auto';
    offsetStyle.bottom = 'auto';
    offsetStyle.left = 'auto';
    offsetStyle.top = 'auto';
    offsetStyle.transform = `translate(${dragPosition.x}px, ${dragPosition.y}px)`;
    offsetStyle.cursor = isDragging ? 'grabbing' : 'grab';
  } else if (offset.x || offset.y) {
    if (position.includes('right')) {
      offsetStyle.right = `calc(24px + ${offset.x}px)`;
    } else if (position.includes('left')) {
      offsetStyle.left = `calc(24px + ${offset.x}px)`;
    }
    
    if (position.includes('bottom')) {
      offsetStyle.bottom = `calc(24px + ${offset.y}px)`;
    } else if (position.includes('top')) {
      offsetStyle.top = `calc(24px + ${offset.y}px)`;
    }
  }
  
  if (draggable && !dragPosition) {
    offsetStyle.cursor = 'grab';
  }
  
  // Load saved position from localStorage
  useEffect(() => {
    if (draggable) {
      const savedPosition = localStorage.getItem('fab-position');
      if (savedPosition) {
        try {
          setDragPosition(JSON.parse(savedPosition));
        } catch (e) {
          console.error('Error loading FAB position:', e);
        }
      }
    }
  }, [draggable]);

  const toggleOptions = () => {
    if (!isDragging) {
      setIsOpen(!isOpen);
    }
  };
  
  const handleMainClick = () => {
    if (!isDragging) {
      if (options && options.length > 0) {
        toggleOptions();
      } else if (onClick) {
        onClick();
      }
    }
  };

  // Draggable handlers
  const handleMouseDown = (e) => {
    if (!draggable) return;
    
    e.preventDefault();
    setIsDragging(true);
    dragStartPos.current = {
      x: e.clientX,
      y: e.clientY
    };
  };

  useEffect(() => {
    if (!draggable) return;

    const handleMouseMove = (e) => {
      if (!isDragging) return;

      const deltaX = e.clientX - dragStartPos.current.x;
      const deltaY = e.clientY - dragStartPos.current.y;

      setDragPosition(prev => {
        const newPos = {
          x: (prev?.x || 0) + deltaX,
          y: (prev?.y || 0) + deltaY
        };
        return newPos;
      });

      dragStartPos.current = {
        x: e.clientX,
        y: e.clientY
      };
    };

    const handleMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
        // Save position to localStorage
        if (dragPosition) {
          localStorage.setItem('fab-position', JSON.stringify(dragPosition));
        }
      }
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, draggable, dragPosition]);
  
  return (
    <div 
      ref={dragRef}
      className={`fab-container ${positionClass} ${isOpen ? 'fab-opened' : ''} ${directionClass} ${draggable ? 'fab-draggable' : ''}`} 
      style={offsetStyle}
      onMouseDown={handleMouseDown}
    >
      {options && options.length > 0 && isOpen && (
        <div className={`fab-options fab-${direction}`}>
          {options.filter(option => option.isVisible !== false).map((option, index) => {
            // Handle both component (Link) and button options
            if (option.component && isValidElement(option.component)) {
              return cloneElement(
                option.component,
                {
                  key: index,
                  className: `fab-option-button fab-${option.color || 'primary'} ${option.component.props.className || ''}`,
                  title: option.tooltip || '',
                  'aria-label': option.tooltip || `Option ${index + 1}`,
                  onClick: (e) => {
                    e.stopPropagation();
                    if (option.component.props.onClick) {
                      option.component.props.onClick(e);
                    }
                    setIsOpen(false);
                  },
                  ...option.componentProps
                },
                <>
                  {React.createElement(option.icon || FaPlus, { className: "fab-option-icon" })}
                  <span className="fab-option-text">{option.tooltip || 'Action'}</span>
                </>
              );
            }
            
            return (
              <button
                key={index}
                className={`fab-option-button fab-${option.color || 'primary'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (option.onClick) {
                    option.onClick();
                  }
                  setIsOpen(false);
                }}
                title={option.tooltip || ''}
                aria-label={option.tooltip || `Option ${index + 1}`}
              >
                {React.createElement(option.icon || FaPlus, { className: "fab-option-icon" })}
                <span className="fab-option-text">{option.tooltip || 'Action'}</span>
              </button>
            );
          })}
        </div>
      )}
      {component && isValidElement(component) ? (
        cloneElement(
          component,
          {
            className: `${baseClass} ${colorClass} ${className} ${isOpen ? 'fab-active' : ''} ${component.props.className || ''}`,
            title: tooltip,
            'aria-label': tooltip || 'Action button',
            onClick: handleMainClick,
            ...componentProps
          },
          React.createElement(icon, {})
        )
      ) : (
        <button
          className={`${baseClass} ${colorClass} ${className} ${isOpen ? 'fab-active' : ''}`}
          onClick={handleMainClick}
          title={tooltip}
          aria-label={tooltip || 'Action button'}
        >
          {React.createElement(icon, {})}
        </button>
      )}
    </div>
  );
};

FAB.propTypes = {
  onClick: PropTypes.func,
  icon: PropTypes.elementType,
  className: PropTypes.string,
  color: PropTypes.oneOf(['primary', 'success', 'danger', 'warning', 'info']),
  tooltip: PropTypes.string,
  position: PropTypes.oneOf(['bottom-right', 'bottom-left', 'top-right', 'top-left']),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.elementType,
      color: PropTypes.oneOf(['primary', 'success', 'danger', 'warning', 'info']),
      tooltip: PropTypes.string,
      onClick: PropTypes.func,
      component: PropTypes.element,
      componentProps: PropTypes.object,
      isVisible: PropTypes.bool
    })
  ),
  direction: PropTypes.oneOf(['up', 'down', 'left', 'right']),
  offset: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number
  }),
  component: PropTypes.element,
  componentProps: PropTypes.object,
  draggable: PropTypes.bool
};

export default FAB;
